import propz from 'propz';
import moment from 'moment';
import { ClubProgrammeBookingItem } from 'Src/helpers/service/admin/clubProgrammes';

export const weekdayOrder: Record<string, number> = {
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
  SUN: 7
};

export const normDay = (d: any) => String(d || '').toUpperCase();

export const fmtAcceptedAt = (iso: any) => {
  if (!iso) return '—';
  const m = moment(iso);
  return m.isValid() ? m.format('ddd, D MMM YYYY, HH:mm') : String(iso);
};

export const fmtTimeRange = (schedule: any) => {
  const startTime = propz.get(schedule, ['startTime'], '');
  const endTime = propz.get(schedule, ['endTime'], '');
  if (startTime && endTime) return `${startTime}–${endTime}`;
  if (startTime) return `${startTime}`;

  const t = propz.get(schedule, ['time'], null);
  if (t) {
    const m = moment(t);
    if (m.isValid()) return m.format('HH:mm');
  }

  return '';
};

export const getAllocationOutcome = (item: any) =>
  String(propz.get(item, ['schoolAllocation', 'allocationOutcome'], 'CLUB') || 'CLUB').toUpperCase();

export const isConfirmedClubItem = (item: any) =>
  !!propz.get(item, ['schoolAllocation', 'isConfirmed'], false) && getAllocationOutcome(item) === 'CLUB';

export const isConfirmedWaitingItem = (item: any) =>
  !!propz.get(item, ['schoolAllocation', 'isConfirmed'], false) && getAllocationOutcome(item) === 'WAITING_LIST';

export const isSelectedClubItem = (item: any) =>
  !!propz.get(item, ['schoolAllocation', 'isSelected'], false) &&
  !propz.get(item, ['schoolAllocation', 'isConfirmed'], false) &&
  getAllocationOutcome(item) === 'CLUB';

export const isSelectedWaitingItem = (item: any) =>
  !!propz.get(item, ['schoolAllocation', 'isSelected'], false) &&
  !propz.get(item, ['schoolAllocation', 'isConfirmed'], false) &&
  getAllocationOutcome(item) === 'WAITING_LIST';

export const sortBookingItemsForLists = (a: any, b: any) => {
  const pa = typeof a.priority === 'number' ? a.priority : 999999;
  const pb = typeof b.priority === 'number' ? b.priority : 999999;
  if (pa !== pb) return pa - pb;

  const aa = a.acceptedAt ? new Date(a.acceptedAt).getTime() : 0;
  const ab = b.acceptedAt ? new Date(b.acceptedAt).getTime() : 0;
  if (aa !== ab) return aa - ab;

  const na = String(propz.get(a, ['student', 'name'], '') || '').toLowerCase();
  const nb = String(propz.get(b, ['student', 'name'], '') || '').toLowerCase();
  return na.localeCompare(nb);
};

export const getPrimaryActionForItem = (
  item: ClubProgrammeBookingItem
): {
  action: 'SELECT_CLUB' | 'SELECT_WAITING_LIST' | 'UNSELECT' | 'REMOVE' | null;
  label: string;
  title: string;
  variant: 'btn-primary' | 'btn-outline-warning' | 'btn-outline-secondary' | 'btn-outline-danger';
  disabled: boolean;
} => {
  const allocationOutcome = String(item.schoolAllocation?.allocationOutcome || '').toUpperCase();
  const isSelected = !!item.schoolAllocation?.isSelected;
  const isConfirmed = !!item.schoolAllocation?.isConfirmed;

  const isThisConfirmedClub = !!item.uiState?.isThisConfirmedClub;
  const isThisConfirmedWaitingList = !!item.uiState?.isThisConfirmedWaitingList;

  const isThisSelectedClub = isSelected && !isConfirmed && allocationOutcome === 'CLUB';
  const isThisSelectedWaitingList = isSelected && !isConfirmed && allocationOutcome === 'WAITING_LIST';

  const isPendingRemove = !!item.uiState?.isPendingRemove;
  const pendingRemoveOutcome = String(item.uiState?.currentOutcome || '').toUpperCase();

  const isPendingRemoveClub = isPendingRemove && pendingRemoveOutcome === 'CLUB';
  const isPendingRemoveWaitingList = isPendingRemove && pendingRemoveOutcome === 'WAITING_LIST';

  if (isPendingRemoveClub || isPendingRemoveWaitingList) {
    return {
      action: 'UNSELECT',
      label: isPendingRemoveClub ? 'Cancel remove' : 'Cancel WL remove',
      title: 'Cancel pending removal',
      variant: 'btn-outline-secondary',
      disabled: false
    };
  }

  if (isThisConfirmedClub) {
    return {
      action: 'REMOVE',
      label: 'Remove from club',
      title: 'Mark removal from club',
      variant: 'btn-outline-danger',
      disabled: false
    };
  }

  if (isThisConfirmedWaitingList) {
    return {
      action: 'REMOVE',
      label: 'Remove from WL',
      title: 'Mark removal from waiting list',
      variant: 'btn-outline-danger',
      disabled: false
    };
  }

  if (isThisSelectedClub || isThisSelectedWaitingList) {
    return {
      action: 'UNSELECT',
      label: 'Unselect',
      title: 'Cancel pending selection',
      variant: 'btn-outline-secondary',
      disabled: false
    };
  }

  if (item.uiState?.canSelectClub) {
    return {
      action: 'SELECT_CLUB',
      label: 'Select club',
      title: 'Allocate to club',
      variant: 'btn-primary',
      disabled: false
    };
  }

  if (item.uiState?.canSelectWaitingList) {
    return {
      action: 'SELECT_WAITING_LIST',
      label: 'Select WL',
      title: 'Allocate to waiting list',
      variant: 'btn-outline-warning',
      disabled: false
    };
  }

  return {
    action: null,
    label: 'Unavailable',
    title: String(item.uiState?.disabledReason || 'No action available'),
    variant: 'btn-outline-secondary',
    disabled: true
  };
};

export const getSecondaryActionForItem = (
  item: ClubProgrammeBookingItem
): {
  action: 'SELECT_CLUB' | 'SELECT_WAITING_LIST' | null;
  label: string;
  title: string;
  variant: 'btn-primary' | 'btn-outline-warning';
  disabled: boolean;
} | null => {
  const allocationOutcome = String(item.schoolAllocation?.allocationOutcome || '').toUpperCase();
  const isSelected = !!item.schoolAllocation?.isSelected;
  const isConfirmed = !!item.schoolAllocation?.isConfirmed;

  const isThisConfirmedClub = !!item.uiState?.isThisConfirmedClub;
  const isThisConfirmedWaitingList = !!item.uiState?.isThisConfirmedWaitingList;

  const isThisSelectedClub = isSelected && !isConfirmed && allocationOutcome === 'CLUB';
  const isThisSelectedWaitingList = isSelected && !isConfirmed && allocationOutcome === 'WAITING_LIST';

  if (!isSelected && !isConfirmed) {
    if (item.uiState?.canSelectClub && item.uiState?.canSelectWaitingList) {
      return {
        action: 'SELECT_WAITING_LIST',
        label: 'Select WL',
        title: 'Allocate to waiting list',
        variant: 'btn-outline-warning',
        disabled: false
      };
    }
  }

  if (isThisConfirmedClub || isThisSelectedClub) {
    if (item.uiState?.canSelectWaitingList) {
      return {
        action: 'SELECT_WAITING_LIST',
        label: 'Move to WL',
        title: 'Change allocation to waiting list',
        variant: 'btn-outline-warning',
        disabled: false
      };
    }
    return null;
  }

  if (isThisConfirmedWaitingList || isThisSelectedWaitingList) {
    if (item.uiState?.canSelectClub) {
      return {
        action: 'SELECT_CLUB',
        label: 'Move to club',
        title: 'Change allocation to club',
        variant: 'btn-primary',
        disabled: false
      };
    }
    return null;
  }

  return null;
};

export const getActionButtonText = (
  action: 'SELECT_CLUB' | 'SELECT_WAITING_LIST' | 'UNSELECT' | 'REMOVE',
  messageId: string,
  idleLabel: string,
  actionKey: string | null
) => {
  return actionKey === `${action}:${String(messageId)}` ? 'Saving...' : idleLabel;
};

export const sortBoardBookingItems = (a: any, b: any) => {
  const pa = propz.get(a, ['priority'], 999);
  const pb = propz.get(b, ['priority'], 999);

  const priorityDiff = (Number(pa) || 999) - (Number(pb) || 999);
  if (priorityDiff !== 0) return priorityDiff;

  const aa = propz.get(a, ['acceptedAt'], null);
  const ab = propz.get(b, ['acceptedAt'], null);

  const at = aa ? new Date(aa).getTime() : 0;
  const bt = ab ? new Date(ab).getTime() : 0;
  if (at !== bt) return at - bt;

  const ua = propz.get(a, ['updatedAt'], null);
  const ub = propz.get(b, ['updatedAt'], null);

  const utA = ua ? new Date(ua).getTime() : 0;
  const utB = ub ? new Date(ub).getTime() : 0;

  return utA - utB;
};



